import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { 
  Zap, 
  Download, 
  Activity, 
  Heart,
  Flame,
  TrendingUp,
  Calendar,
  Loader2,
  AlertCircle,
  Clock
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONE_COLORS = [
  "#808080", // Zone 1 - Gray
  "#0080FF", // Zone 2 - Blue
  "#00FF80", // Zone 3 - Green
  "#D4FF00", // Zone 4 - Volt
  "#FFAA00", // Zone 5 - Orange
  "#FF0055", // Zone 6 - Red
  "#8000FF", // Zone 7 - Purple
];

export default function SharedAnalysis() {
  const { t } = useTranslation();
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [pdfLang, setPdfLang] = useState("de");

  useEffect(() => {
    fetchSharedAnalysis();
  }, [shareToken]);

  const fetchSharedAnalysis = async () => {
    try {
      const res = await fetch(`${API}/shared/${shareToken}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Fehler beim Laden");
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/shared/${shareToken}/pdf?lang=${pdfLang}`);
      if (!res.ok) throw new Error("PDF-Download fehlgeschlagen");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `VeloMetrics_${data?.analysis?.athlete_name || "Analyse"}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(t('analysis.downloadPdf') + " ✓");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("de-DE");
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-volt animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('analysis.loading', 'Analyse wird geladen...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center glass-card rounded-xl p-8 max-w-md">
          <AlertCircle className="h-12 w-12 text-heart-red mx-auto mb-4" />
          <h2 className="font-display text-xl font-bold uppercase mb-2">Link ungültig</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/">
            <Button className="bg-volt text-black hover:bg-volt/90">
              Zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const analysis = data?.analysis;
  const athleteProfile = data?.athlete_profile;
  const expiresAt = data?.expires_at;

  // Prepare zone data for chart
  const zoneData = analysis?.power_zones ? Object.entries(analysis.power_zones).map(([zoneKey, data], idx) => {
    const zoneNum = zoneKey.replace('zone', '');
    const isZone7 = zoneNum === "7";
    const zone6Max = analysis.power_zones["zone6"]?.max || data.min;
    return {
      zone: `Z${zoneNum}`,
      zoneNum: zoneNum,
      min: data.min,
      max: isZone7 ? zone6Max * 1.15 : data.max, // Zone 7 bar slightly larger than Zone 6
      displayMax: isZone7 ? `>${zone6Max}` : data.max,
      isZone7,
      name: data.name
    };
  }) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-8 w-8 text-volt" />
              <span className="font-display font-bold text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="minimal" />
              <Link to="/">
                <Button variant="outline" size="sm" className="border-white/20">
                  {t('landing.heroButton', 'Eigene Analyse starten')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="shared-analysis-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <Clock className="h-4 w-4" />
              <span>Geteilte Analyse • Gültig bis {formatDate(expiresAt)}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase">
              {analysis?.athlete_name || "Analyse"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {analysis?.test_type === "20min" ? t('upload.testType20min') : t('upload.testTypeRamp')} • {formatDate(analysis?.athlete_data?.test_date || analysis?.created_at)}
            </p>
          </div>

          {/* PDF Download */}
          <div className="glass-card rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <select
                value={pdfLang}
                onChange={(e) => setPdfLang(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm"
                data-testid="pdf-lang-select"
              >
                <option value="de">{t('analysis.german', 'Deutsch')}</option>
                <option value="en">{t('analysis.english', 'English')}</option>
              </select>
              <span className="text-sm text-muted-foreground">{t('analysis.pdfLanguage', 'PDF-Sprache')}</span>
            </div>
            <Button
              onClick={downloadPdf}
              disabled={downloading}
              className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span>{t('analysis.downloading', 'PDF wird erstellt...')}</span>
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  <span>{t('analysis.downloadPdf', 'PDF herunterladen')}</span>
                </>
              )}
            </Button>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card rounded-xl p-6 stat-card">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-5 w-5 text-volt" />
                <span className="data-label">FTP</span>
              </div>
              <p className="number-display text-3xl text-volt">
                {Number(analysis?.ftp || 0).toFixed(0)}
                <span className="text-sm ml-1">W</span>
              </p>
              {analysis?.ftp_per_kg && (
                <p className="text-xs text-muted-foreground mt-1">
                  {Number(analysis.ftp_per_kg).toFixed(2)} W/kg
                </p>
              )}
            </div>

            <div className="glass-card rounded-xl p-6 stat-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-cyan" />
                <span className="data-label">VO2max</span>
              </div>
              <p className="number-display text-3xl text-cyan">
                {Number(analysis?.vo2max || 0).toFixed(1)}
                <span className="text-sm ml-1">ml/kg</span>
              </p>
              {analysis?.vo2max_absolute && (
                <p className="text-xs text-muted-foreground mt-1">
                  {Number(analysis.vo2max_absolute).toFixed(2)} L/min
                </p>
              )}
            </div>

            <div className="glass-card rounded-xl p-6 stat-card">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="h-5 w-5 text-carb-orange" />
                <span className="data-label">VT1</span>
              </div>
              <p className="number-display text-3xl text-carb-orange">
                {Number(analysis?.vt1 || 0).toFixed(0)}
                <span className="text-sm ml-1">W</span>
              </p>
            </div>

            <div className="glass-card rounded-xl p-6 stat-card">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-5 w-5 text-heart-red" />
                <span className="data-label">VT2</span>
              </div>
              <p className="number-display text-3xl text-heart-red">
                {Number(analysis?.vt2 || 0).toFixed(0)}
                <span className="text-sm ml-1">W</span>
              </p>
            </div>
          </div>

          {/* Power Zones */}
          <div className="glass-card rounded-xl p-6 mb-8">
            <h2 className="font-display text-xl font-bold uppercase mb-6">{t('analysis.powerZones', 'Leistungszonen')}</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={zoneData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#666" />
                  <YAxis type="category" dataKey="zone" stroke="#666" width={40} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    formatter={(value, name, props) => {
                      const payload = props.payload;
                      if (payload.isZone7) {
                        return [`> ${payload.min} W`, 'Bereich'];
                      }
                      return [`${payload.min} - ${payload.displayMax} W`, 'Bereich'];
                    }}
                  />
                  <Bar dataKey="max" fill="#D4FF00">
                    {zoneData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={ZONE_COLORS[index] || "#D4FF00"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Zone Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-6">
              {analysis?.power_zones && Object.entries(analysis.power_zones).map(([zoneKey, data], idx) => {
                const zoneNum = zoneKey.replace('zone', '');
                const isZone7 = zoneNum === "7";
                const zone6Max = analysis.power_zones["zone6"]?.max || data.min;
                return (
                  <div 
                    key={zoneKey} 
                    className="p-3 rounded-lg text-center"
                    style={{ backgroundColor: `${ZONE_COLORS[idx]}20`, borderColor: ZONE_COLORS[idx], borderWidth: 1 }}
                  >
                    <div className="text-xs text-muted-foreground mb-1">Zone {zoneNum}</div>
                    <div className="font-bold" style={{ color: ZONE_COLORS[idx] }}>
                      {isZone7 ? `>${zone6Max}W` : `${data.min}-${data.max}W`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Energy & Nutrition */}
          {analysis?.carb_consumption && Object.keys(analysis.carb_consumption).length > 0 && (
            <div className="glass-card rounded-xl p-6 mb-8">
              <h2 className="font-display text-xl font-bold uppercase mb-6 flex items-center gap-2">
                <Flame className="h-5 w-5 text-carb-orange" />
                {t('analysis.energy.title', 'Energie & Kohlenhydratverbrauch')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Object.entries(analysis.carb_consumption).map(([zoneName, data], idx) => {
                  const zoneColor = ZONE_COLORS[idx] || "#D4FF00";
                  
                  return (
                    <div 
                      key={zoneName}
                      className="p-4 rounded-lg border"
                      style={{ borderColor: `${zoneColor}50`, backgroundColor: `${zoneColor}10` }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold" style={{ color: zoneColor }}>{data.description || zoneName}</span>
                        <span className="text-xs text-muted-foreground">{data.watts}W</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Kalorien</span>
                          <span className="font-medium">{data.kcal_per_hour?.toFixed(0) || "-"} kcal/h</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Kohlenhydrate</span>
                          <span className="font-medium text-carb-orange">{data.carbs_g_per_hour?.toFixed(0) || "-"} g/h</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Fett</span>
                          <span className="font-medium">{data.fat_g_per_hour?.toFixed(0) || "-"} g/h</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-white/10">
                          <span>CHO {data.cho_percent?.toFixed(0)}%</span>
                          <span>Fett {data.fat_percent?.toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Assessment */}
          {analysis?.ai_assessment && (
            <div className="glass-card rounded-xl p-6 mb-8 border border-volt/30">
              <h2 className="font-display text-xl font-bold uppercase mb-4">{t('analysis.aiAssessment', 'KI-Bewertung')}</h2>
              <p className="text-muted-foreground whitespace-pre-line">{analysis.ai_assessment}</p>
            </div>
          )}

          {/* CTA */}
          <div className="glass-card rounded-xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold uppercase mb-4">
              Eigene Analyse erstellen?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Lade deine FIT-Datei hoch und erhalte eine vollständige Leistungsanalyse mit individuellen Trainingszonen.
            </p>
            <Link to="/">
              <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider text-lg px-8 py-6">
                <span>Jetzt starten</span>
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
